/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Card,
  Chip,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Add as AddIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  Category as CategoryIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Select from "react-select";

function AddPrecautionDialog({
  open,
  onClose,
  onSave,
  categories,
  categoriesLoading,
  isLoading,
  initialData = null,
  isEditMode = false,
  preSelectedCategoryId = null,
}) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [precautionName, setPrecautionName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Populate fields when editing or when pre-selected category is provided
  useEffect(() => {
    if (open) {
      if (isEditMode && initialData) {
        setPrecautionName(initialData.name || "");
        // Find the category from the list
        const category = categories?.find(
          (cat) => cat.id === initialData.category_id
        );
        if (category) {
          setSelectedCategory({
            value: category.id,
            label: category.name,
          });
        }
      } else if (preSelectedCategoryId && !isEditMode) {
        // Set pre-selected category for adding new precaution
        const category = categories?.find(
          (cat) => cat.id === preSelectedCategoryId
        );
        if (category) {
          setSelectedCategory({
            value: category.id,
            label: category.name,
          });
        }
      }
    }
  }, [isEditMode, initialData, categories, open, preSelectedCategoryId]);

  const resetFields = () => {
    setPrecautionName("");
    setSelectedCategory(null);
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const handleSave = () => {
    if (precautionName.trim() && selectedCategory) {
      onSave(precautionName, selectedCategory.value, resetFields);
    }
  };

  const categoryOptions =
    categories?.map((category) => ({
      value: category.id,
      label: category.name,
    })) || [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
          borderBottom: 1,
          borderColor: theme.palette.checklist.border.subtle,
          pb: 2,
          pt: 3,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: isEditMode ? "primary.main" : "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: isEditMode
                ? "primary.contrastText"
                : "success.contrastText",
              boxShadow: 3,
            }}
          >
            {isEditMode ? (
              <EditIcon sx={{ fontSize: 32 }} />
            ) : (
              <AddIcon sx={{ fontSize: 32 }} />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {isEditMode
                ? t("precautions.editDialogTitle")
                : t("precautions.addDialogTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode
                ? t("precautions.editDialogSubtitle")
                : preSelectedCategoryId && selectedCategory
                  ? t("precautions.addDialogSubtitleWithCategory", {
                      categoryName: selectedCategory.label,
                    })
                  : t("precautions.addDialogSubtitle")}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              bgcolor: theme.palette.checklist.interactive.hover,
              "&:hover": {
                bgcolor: theme.palette.checklist.interactive.selected,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, bgcolor: theme.palette.checklist.surface.elevated }}
      >
        <Box sx={{ p: 3 }}>
          {/* Section 1: Basic Information */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              border: 1,
              borderColor: alpha(theme.palette.success.main, 0.2),
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
                borderBottom: 1,
                borderColor: alpha(theme.palette.success.main, 0.1),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <InfoIcon sx={{ color: "success.main", fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="600">
                    {t("hazards.basicInformation")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("precautions.basicInformationDesc")}
                  </Typography>
                </Box>
                <Chip
                  label="1"
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: "success.main",
                    fontWeight: "bold",
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ p: 3 }}>
              <TextField
                label={t("precautions.newPrecautionLabel")}
                variant="outlined"
                value={precautionName}
                onChange={(e) => setPrecautionName(e.target.value)}
                fullWidth
                required
                placeholder={t("precautions.newPrecautionPlaceholder")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: theme.shadows[2],
                    },
                    "&.Mui-focused": {
                      boxShadow: theme.shadows[4],
                    },
                  },
                }}
                aria-label={t("precautions.newPrecautionAria")}
              />
            </Box>
          </Card>

          {/* Section 2: Category Selection - Only show when no pre-selected category */}
          {!preSelectedCategoryId && (
            <Card
              elevation={0}
              sx={{
                border: 1,
                borderColor: alpha(theme.palette.success.main, 0.2),
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.03)} 100%)`,
                  borderBottom: 1,
                  borderColor: alpha(theme.palette.success.main, 0.1),
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CategoryIcon
                      sx={{ color: "success.main", fontSize: 20 }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="600">
                      {t("precautions.categorySelection")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("precautions.categorySelectionDesc")}
                    </Typography>
                  </Box>
                  <Chip
                    label="2"
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: "success.main",
                      fontWeight: "bold",
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ p: 3 }}>
                <Select
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  options={categoryOptions}
                  placeholder={t("precautions.selectCategoryPlaceholder")}
                  isSearchable
                  isLoading={categoriesLoading}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: 8,
                      borderColor: theme.palette.divider,
                      minHeight: 48,
                      "&:hover": {
                        borderColor: theme.palette.success.main,
                      },
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected
                        ? theme.palette.success.main
                        : state.isFocused
                          ? alpha(theme.palette.success.main, 0.1)
                          : "white",
                      color: state.isSelected
                        ? "white"
                        : theme.palette.text.primary,
                    }),
                  }}
                />
              </Box>
            </Card>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          gap: 2,
          bgcolor: theme.palette.checklist.surface.elevated,
          borderTop: 1,
          borderColor: theme.palette.checklist.border.subtle,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t("hazards.requiredFieldsNote")}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: "medium",
            }}
          >
            {t("precautions.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color={isEditMode ? "primary" : "success"}
            disabled={isLoading || !precautionName.trim() || !selectedCategory}
            startIcon={
              isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : isEditMode ? (
                <EditIcon />
              ) : (
                <AddIcon />
              )
            }
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              textTransform: "none",
              fontWeight: "bold",
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4,
                transform: "translateY(-1px)",
              },
            }}
            aria-label={
              isEditMode
                ? t("precautions.editButtonAria")
                : t("precautions.addButtonAria")
            }
          >
            {isEditMode ? t("precautions.update") : t("precautions.save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default AddPrecautionDialog;
